'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import CopyToClipboard from 'react-copy-to-clipboard';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import { getTicketIdByExternalId } from '@/server-functions/csForm/getTicketIdByExternalId';
import Spinner from '@/ui/components/shared/Spinner/Spinner';
import Alert from '@/ui/components/Alert/Alert';
import Button from '@/ui/components/shared/Button';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { isSupportAvailable } from '@/utils/supportHours';
import { buildTrackingId } from '@/utils/tracking-helper';
import Checkmark from '../../../../../../public/images/checkmark-yellow-black-border.svg';
import CopyIcon from '../../../../../../public/images/copy-icon.svg';
import OpenLiveChatAndSendMessage from './LiveChat';

function CopyButton({ text }: Readonly<{ text: string }>) {
  const t = useTranslations('CopyButton');
  const [copied, setCopied] = useState(false);

  return (
    <CopyToClipboard
      text={text}
      onCopy={() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <Button className="flex items-center gap-2 px-3 py-[6px]">
        <Image src={CopyIcon} alt={t('copy')} width={20} height={20} aria-hidden />
        {copied ? t('copied') : t('copy')}
      </Button>
    </CopyToClipboard>
  );
}

type Props = {
  readonly ticketId: number | null;
  readonly hotlineAvailabilityNotice?: string;
  readonly trackingPrefix?: string;
};

export default function ConfirmationPageChat({
  ticketId,
  hotlineAvailabilityNotice,
  trackingPrefix,
}: Props) {
  const t = useTranslations('CSForm');
  const { country } = useLocalization();
  const isAvailable = isSupportAvailable(country);
  const [chatOpen, setChatOpen] = useState(false);

  if (!ticketId) {
    return (
      <div
        className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 pb-20 pt-12 text-center md:pt-16"
        data-testid="thank-you-generic"
      >
        <Image
          src={Checkmark}
          alt=""
          width={32}
          height={32}
          className="mb-8 h-14 w-14 sm:h-20 sm:w-20 md:h-14 md:w-14"
        />
        <h1 className="mb-3 text-2xl font-bold md:mb-6 md:text-4xl">{t('genericThankYouTitle')}</h1>
        <p className="whitespace-pre-line sm:text-lg">{t('genericThankYouBody')}</p>
      </div>
    );
  }

  return (
    <div
      className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 pb-20 pt-12 md:pt-16"
      data-testid="thank-you-handover"
    >
      {/* Header */}
      <div className="mb-10 flex flex-col items-center">
        <Image
          src={Checkmark}
          alt=""
          width={32}
          height={32}
          className="mb-8 h-14 w-14 sm:h-20 sm:w-20 md:h-14 md:w-14"
        />
        <h1 className="mb-3 text-2xl font-bold md:mb-6 md:text-4xl">
          {t('phoneConfirmationPageHeader')}
        </h1>
        <p className="whitespace-pre-line sm:text-lg">{t('phoneConfirmationPageInfoText')}</p>
      </div>

      {/* Ticket */}
      <div
        className="mb-8 w-full rounded-md border border-neutral-40 bg-gray-50 p-4"
        data-testid="ticket-reference"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{t('yourTicketId')}</h2>
          <CopyButton text={String(ticketId)} />
        </div>

        <div className="mb-3 rounded-sm border border-gray-300 bg-white py-6 text-center text-xl font-bold">
          {`#${ticketId}`}
        </div>

        {/* Alert */}
        {isAvailable === false ? (
          <Alert className="flex rounded-sm border border-red-800 bg-red-800/10 text-red-800">
            <div className="mt-[0.25em]">
              <HiOutlineExclamationCircle className="mx-1 block h-4 w-4" />
            </div>
            <div>
              <span className="font-bold">{t('alertImportant')}</span> {t('alertClosedText')}
            </div>
          </Alert>
        ) : (
          <Alert type="warning-2" className="flex">
            <div className="mt-[0.25em]">
              <HiOutlineExclamationCircle className="mx-1 block h-4 w-4" />
            </div>
            <div>
              <span className="font-bold">{t('alertImportant')}</span> {t('alertText')}
            </div>
          </Alert>
        )}
      </div>

      {/* Start Chat CTA */}
      <div className="mb-10 flex w-full flex-col items-center">
        {!chatOpen && isAvailable && (
          <Button
            className="mb-3 block w-full sm:max-w-sm"
            onClick={() => setChatOpen(true)}
            data-tile-element-id={buildTrackingId(trackingPrefix, 'chat-open')}
          >
            {t('chatStartButton')}
          </Button>
        )}
        {isAvailable && (
          <p className="text-center text-sm text-gray-500">{t('chatStartInfoText')}</p>
        )}
        {chatOpen && <OpenLiveChatAndSendMessage ticketId={ticketId ?? undefined} />}
      </div>

      {/* Availability schedule */}
      <div className="w-full">
        <div className="mb-2 w-full rounded-md border border-neutral-40 bg-gray-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-lg font-bold">{t('chatAvailability')}</h2>
            {isAvailable ? (
              <span className="bg-green-800 rounded-md p-1 px-2 text-sm font-bold uppercase text-white">
                {t('chatAvailableLabel')}
              </span>
            ) : (
              <span className="rounded-md bg-red-800 p-1 px-2 text-sm font-bold uppercase text-white">
                {t('hotlineClosedLabel')}
              </span>
            )}
          </div>
          <ul>
            <li className="border-b border-gray-200 py-2 sm:flex sm:justify-between">
              <span className="font-semibold">{t('hotlineAvailabilityDaysRowOne')} </span>
              <span>{t('hotlineAvailabilityTimeRowOne')}</span>
            </li>
            <li className="py-2 sm:flex sm:justify-between">
              <span className="font-semibold">{t('hotlineAvailabilityDaysRowTwo')} </span>
              <span>{t('hotlineAvailabilityTimeRowTwo')}</span>
            </li>
          </ul>
        </div>
        {hotlineAvailabilityNotice && (
          <p
            className="mb-10 px-5 text-sm text-gray-500"
            dangerouslySetInnerHTML={{ __html: hotlineAvailabilityNotice }}
          />
        )}
      </div>

      <Alert type="success" className="mb-8 flex w-full">
        <div className="ml-1 mr-2 mt-[0.25em]">
          <EnvelopeIcon className="h-4 w-4" />
        </div>
        <div className="flex-grow-0">{t('alertConfirmationEmailSent')}</div>
      </Alert>

      {/* Back */}
      <button
        type="button"
        onClick={() => window.history.back()}
        className="text-sm font-semibold text-neutral-100 underline-offset-2 hover:underline"
      >
        {'< '}
        {t('Back')}
      </button>
    </div>
  );
}
